import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Switch, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Alert, Collapse, Stack,
  LinearProgress, Snackbar, Chip, IconButton, Divider, Link, Tooltip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger';

const logger = createLogger('ReportGenConfigEditor');
const ctx = (extra) => adminLogContext('file_and_report_management', extra);

// ---- helpers ---------------------------------------------------------------
const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const deepMerge = (base, override) => {
  const out = { ...(base || {}) };
  for (const [k, v] of Object.entries(override || {})) {
    out[k] = isPlainObject(v) && isPlainObject(out[k]) ? deepMerge(out[k], v) : v;
  }
  return out;
};

const getPath = (obj, path) => path.reduce((o, k) => (isPlainObject(o) ? o[k] : undefined), obj);

const setPath = (obj, path, val) => {
  const [h, ...rest] = path;
  const base = isPlainObject(obj) ? { ...obj } : {};
  base[h] = rest.length ? setPath(base[h] || {}, rest, val) : val;
  return base;
};

const unsetPath = (obj, path) => {
  const [h, ...rest] = path;
  if (!isPlainObject(obj) || !(h in obj)) return obj;
  const base = { ...obj };
  if (!rest.length) { delete base[h]; return base; }
  const child = unsetPath(base[h], rest);
  if (isPlainObject(child) && Object.keys(child).length === 0) delete base[h];
  else base[h] = child;
  return base;
};

const countLeaves = (o) =>
  isPlainObject(o) ? Object.values(o).reduce((a, v) => a + countLeaves(v), 0) : 1;

const ACRONYMS = { api: 'API', http: 'HTTP', mb: 'MB', dpi: 'DPI', ttl: 'TTL', jpeg: 'JPEG', toc: 'TOC', db: 'DB', url: 'URL', id: 'ID' };
const humanize = (k) =>
  String(k).split('_')
    .map((w, i) => ACRONYMS[w] || (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

const THINKING_LEVELS = ['minimal', 'low', 'medium', 'high'];
const pretty = (o) => JSON.stringify(o ?? {}, null, 2);

/**
 * Structured editor for the GLOBAL report-generation default
 * (app_settings.report_gen_default_config). Renders one labeled control per knob,
 * grouped by config section, driven off the code default's shape. Only fields that
 * differ from the code default are persisted (sparse override). Resolution at run
 * time: code default -> this global default -> a task's own config.
 */
export default function ReportGenConfigEditor() {
  const [codeDefault, setCodeDefault] = useState({});
  const [savedOverride, setSavedOverride] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showEffective, setShowEffective] = useState(false);
  const [snack, setSnack] = useState('');

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await apiService.getReportGenDefaultConfig();
      const ov = data?.global_default || {};
      setCodeDefault(data?.code_default || {});
      setSavedOverride(ov);
      setOverrides(ov);
      logger.info('Report generation default config loaded', ctx({
        action: 'load_report_gen_config',
        override_count: countLeaves(ov),
      }));
    } catch (e) {
      setLoadError('Could not load config. Check that you are signed in as admin.');
      logger.apiFailure('Failed to load report generation config', ctx({ action: 'load_report_gen_config' }), e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(overrides) !== JSON.stringify(savedOverride);
  const overrideCount = useMemo(() => countLeaves(overrides), [overrides]);
  const effective = useMemo(() => deepMerge(codeDefault, overrides), [codeDefault, overrides]);
  const hasSchema = Object.keys(codeDefault).length > 0;

  const isOverridden = (path) => getPath(overrides, path) !== undefined;
  const resetField = (path) => setOverrides((p) => unsetPath(p, path));
  const setField = (path, defVal, raw) => {
    setOverrides((prev) => {
      if (raw === '' || raw === undefined || raw === null) return unsetPath(prev, path);
      let v = raw;
      if (typeof defVal === 'number') {
        v = Number(raw);
        if (Number.isNaN(v)) return prev;
      }
      if (v === defVal) return unsetPath(prev, path);
      return setPath(prev, path, v);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.saveReportGenDefaultConfig(overrides);
      const saved = res?.global_default ?? overrides;
      setSavedOverride(saved);
      setOverrides(saved);
      setSnack('Saved. Applies to the next run of every county.');
      logger.info('Report generation default config saved', ctx({
        action: 'save_report_gen_config',
        override_count: countLeaves(saved),
      }));
    } catch (e) {
      setSnack(`Save failed: ${e?.message || 'unknown error'}`);
      logger.apiFailure('Failed to save report generation config', ctx({ action: 'save_report_gen_config' }), e);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await apiService.clearReportGenDefaultConfig();
      setSavedOverride({});
      setOverrides({});
      setSnack('Override cleared — reverted to pure code defaults.');
      logger.info('Report generation default config cleared', ctx({ action: 'clear_report_gen_config' }));
    } catch (e) {
      setSnack(`Clear failed: ${e?.message || 'unknown error'}`);
      logger.apiFailure('Failed to clear report generation config', ctx({ action: 'clear_report_gen_config' }), e);
    } finally {
      setSaving(false);
    }
  };

  // ---- field renderers ----
  const overriddenCaption = (path, defVal) => (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 22, mt: 0.25 }}>
      <Typography variant="caption" color="warning.main">overridden</Typography>
      <Tooltip title={`Reset to default (${String(defVal)})`}>
        <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }} onClick={() => resetField(path)}>
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
  const defaultCaption = (
    <Box sx={{ minHeight: 22, mt: 0.25 }}>
      <Typography variant="caption" color="text.disabled">default</Typography>
    </Box>
  );

  const renderField = (path, key, defVal) => {
    const overridden = isOverridden(path);
    const val = overridden ? getPath(overrides, path) : defVal;
    const caption = overridden ? overriddenCaption(path, defVal) : defaultCaption;

    if (typeof defVal === 'boolean') {
      return (
        <Box key={path.join('.')} sx={{ width: 230 }}>
          <FormControlLabel
            control={<Switch size="small" checked={!!val}
              onChange={(e) => setField(path, defVal, e.target.checked)} />}
            label={<Typography variant="body2">{humanize(key)}</Typography>}
          />
          {caption}
        </Box>
      );
    }

    if (key.endsWith('thinking_level')) {
      return (
        <Box key={path.join('.')} sx={{ width: 230 }}>
          <FormControl size="small" fullWidth color={overridden ? 'warning' : 'primary'} focused={overridden}>
            <InputLabel>{humanize(key)}</InputLabel>
            <Select label={humanize(key)} value={val}
              onChange={(e) => setField(path, defVal, e.target.value)}
              MenuProps={{
                style: { zIndex: 1700 },
                slotProps: { root: { sx: { zIndex: 1700 } } },
              }}>
              {THINKING_LEVELS.map((lvl) => <MenuItem key={lvl} value={lvl}>{lvl}</MenuItem>)}
            </Select>
          </FormControl>
          {caption}
        </Box>
      );
    }

    const isNum = typeof defVal === 'number';
    return (
      <Box key={path.join('.')} sx={{ width: 230 }}>
        <TextField
          label={humanize(key)}
          size="small" fullWidth
          type={isNum ? 'number' : 'text'}
          value={val}
          onChange={(e) => setField(path, defVal, e.target.value)}
          color={overridden ? 'warning' : 'primary'}
          focused={overridden || undefined}
          inputProps={isNum ? { step: Number.isInteger(defVal) ? 1 : 0.1, min: 0 } : {}}
        />
        {caption}
      </Box>
    );
  };

  const renderBody = (prefix, obj) => {
    const entries = Object.entries(obj);
    const leaves = entries.filter(([, v]) => !isPlainObject(v));
    const groups = entries.filter(([, v]) => isPlainObject(v));
    return (
      <>
        {leaves.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {leaves.map(([k, v]) => renderField([...prefix, k], k, v))}
          </Box>
        )}
        {groups.map(([k, v]) => (
          <Box key={[...prefix, k].join('.')}
            sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 1, fontWeight: 600 }}>{humanize(k)}</Typography>
            {renderBody([...prefix, k], v)}
          </Box>
        ))}
      </>
    );
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip
          size="small"
          color={overrideCount ? 'warning' : 'default'}
          variant={overrideCount ? 'filled' : 'outlined'}
          label={overrideCount ? `${overrideCount} override${overrideCount > 1 ? 's' : ''}` : 'No overrides — using code defaults'}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Link component="button" type="button" variant="body2" onClick={() => setShowEffective((v) => !v)}>
          {showEffective ? 'Hide' : 'Show'} effective result (JSON)
        </Link>
      </Stack>

      <Collapse in={showEffective}>
        <Box component="pre" sx={{
          m: 0, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1,
          fontSize: 12, maxHeight: 280, overflow: 'auto',
        }}>{pretty(effective)}</Box>
      </Collapse>

      {!hasSchema && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The tunable controls appear once the worker has booted and published the
          config baseline. Start the worker, then reopen this panel.
        </Alert>
      )}

      {hasSchema && (
        <Stack spacing={2}>
          {Object.entries(codeDefault).map(([k, v]) => (
            <Paper key={k} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{humanize(k)}</Typography>
              {isPlainObject(v)
                ? renderBody([k], v)
                : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {renderField([k], k, v)}
                  </Box>
                )}
            </Paper>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" onClick={() => setOverrides(savedOverride)} disabled={!dirty || saving}>
          Reset edits
        </Button>
        <Button color="error" onClick={handleClear}
          disabled={saving || Object.keys(savedOverride).length === 0}>
          Clear all overrides
        </Button>
      </Stack>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}